// Utility functions for handling localization in the application
import React, { createContext, useState, useContext, useEffect } from 'react';
import { LANGUAGE_CHANGE_EVENT } from '../components/layout/Header';

/**
 * Safely gets localized text from an object with language keys
 * @param {Object|string|number|boolean} textObj - Object with language keys or primitive value
 * @param {string} language - Current language code (e.g. 'ru', 'en')
 * @param {string} defaultValue - Default value to return if textObj is empty
 * @returns {string} - Localized text
 */
export const getLocalizedText = (textObj, language = 'ru', defaultValue = '') => {
  if (!textObj) return defaultValue;
  
  // If it's already a string or other primitive, return its string representation
  if (typeof textObj !== 'object') return String(textObj);
  
  // Try to get text in the current language, fallback to English, then default
  return (language && textObj[language]) || textObj['en'] || defaultValue;
};

/**
 * React Context for localization
 */

const LocalizationContext = createContext({
  language: 'ru',
  getLocalizedText: () => '',
  setLanguage: () => {}
});

export const LocalizationProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Get saved language or default to Russian
    return localStorage.getItem('portfolioLanguage') || 'ru';
  });

  // Update language when language change event fires
  useEffect(() => {
    const handleLanguageChange = (event) => {
      if (event?.detail?.language) {
        setLanguage(event.detail.language);
      } else {
        const newLang = localStorage.getItem('portfolioLanguage');
        if (newLang && newLang !== language) {
          setLanguage(newLang);
        }
      }
    };

    window.addEventListener('storage', handleLanguageChange);
    window.addEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);

    return () => {
      window.removeEventListener('storage', handleLanguageChange);
      window.removeEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
    };
  }, [language]);

  // Create a localized text function that uses the current language
  const localizedTextFn = (textObj, defaultValue = '') => {
    return getLocalizedText(textObj, language, defaultValue);
  };

  return (
    <LocalizationContext.Provider value={{
      language,
      getLocalizedText: localizedTextFn,
      setLanguage
    }}>
      {children}
    </LocalizationContext.Provider>
  );
};

export const useLocalization = () => useContext(LocalizationContext);
